/**
 * Icon 공통 모듈
 * @module shared/components/Icons
 * @author Lian
 */

import {
  MouseEventHandler,
  Ref,
  useEffect,
  useRef,
  useState,
} from 'react';

import iconAttachment from '@/assets/common/image/icon-attachment.png';
import iconBin from '@/assets/common/image/icon-bin.png';
import iconBoardDark from '@/assets/common/image/icon-board-dark.png';
import iconBoard from '@/assets/common/image/icon-board.png';
import iconBox from '@/assets/common/image/icon-box.png';
import iconDownDark from '@/assets/common/image/icon-download-dark.png';
import iconDownloadWithText from '@/assets/common/image/icon-download-with-text.png';
import iconDown from '@/assets/common/image/icon-download.png';
import iconExcel from '@/assets/common/image/icon-excel.png';
import iconHamburgerDark from '@/assets/common/image/icon-hamburger-dark.png';
import iconHamburger from '@/assets/common/image/icon-hamburger.png';
import iconLock from '@/assets/common/image/icon-lock.png';
import iconLogin from '@/assets/common/image/icon-login.png';
import iconLogoDark from '@/assets/common/image/icon-logo-dark.png';
import iconLogo from '@/assets/common/image/icon-logo.png';
import iconMenuLogDark from '@/assets/common/image/icon-menu-log-dark.png';
import iconMenuLog from '@/assets/common/image/icon-menu-log.png';
import iconMenuUserInfoDark from '@/assets/common/image/icon-menu-user-information-dark.png';
import iconMenuUserInfo from '@/assets/common/image/icon-menu-user-information.png';
import iconModalConfirmDark from '@/assets/common/image/icon-modal-confirm-dark.png';
import iconModalConfirm from '@/assets/common/image/icon-modal-confirm.png';
import iconModalErrorDark from '@/assets/common/image/icon-modal-error-dark.png';
import iconModalError from '@/assets/common/image/icon-modal-error.png';
import iconModalInfoDark from '@/assets/common/image/icon-modal-information-dark.png';
import iconModalInfo from '@/assets/common/image/icon-modal-information.png';
import iconPin from '@/assets/common/image/icon-pin.png';
import iconProfileDark from '@/assets/common/image/icon-profile-dark.png';
import iconProfile from '@/assets/common/image/icon-profile.png';
import iconSearch from '@/assets/common/image/icon-search.png';
import iconWrite from '@/assets/common/image/icon-write.png';
import iconDashboardBarChart from '@/assets/manage/image/icon-dashboard-bar-chart.png';
import iconDashboardLink from '@/assets/manage/image/icon-dashboard-link.png';
import iconManageDark from '@/assets/manage/image/icon-manage-dark.png';
import iconManage from '@/assets/manage/image/icon-manage.png';
import iconMenuCodeDark from '@/assets/manage/image/icon-menu-code-dark.png';
import iconMenuCode from '@/assets/manage/image/icon-menu-code.png';
import iconMenuDashboardDark from '@/assets/manage/image/icon-menu-dashboard-dark.png';
import iconMenuDashboard from '@/assets/manage/image/icon-menu-dashboard.png';
import iconMenuDataDark from '@/assets/manage/image/icon-menu-data-dark.png';
import iconMenuDataGroupDark from '@/assets/manage/image/icon-menu-data-group-dark.png';
import iconMenuDataGroup from '@/assets/manage/image/icon-menu-data-group.png';
import iconMenuData from '@/assets/manage/image/icon-menu-data.png';
import iconMenuLinkDark from '@/assets/manage/image/icon-menu-link-dark.png';
import iconMenuLink from '@/assets/manage/image/icon-menu-link.png';
import iconMenuRoleDark from '@/assets/manage/image/icon-menu-role-dark.png';
import iconMenuRole from '@/assets/manage/image/icon-menu-role.png';
import iconMenuStatisticDark from '@/assets/manage/image/icon-menu-statistic-dark.png';
import iconMenuStatistic from '@/assets/manage/image/icon-menu-statistic.png';
import iconMenuToolDark from '@/assets/manage/image/icon-menu-tool-dark.png';
import iconMenuTool from '@/assets/manage/image/icon-menu-tool.png';
import iconMenuUpdateDark from '@/assets/manage/image/icon-menu-update-dark.png';
import iconMenuUpdate from '@/assets/manage/image/icon-menu-update.png';
import iconMainPageLogoDark from '@/assets/portal/image/icon-logo-dark.png';
import iconMainPageLogo from '@/assets/portal/image/icon-logo.png';
import iconLogout from '@/assets/portal/image/icon-logout.png';
import iconShortcut from '@/assets/portal/image/icon-shortcut.png';
import cn from '@/shared/components/lib/utils.ts';
import useTheme from '@/shared/hooks/useTheme.ts';

/**
 * icon return element
 * @param className className
 * @param url icon 이미지
 * @returns HTMLDivElement
 * @author Lian
 */
const iconDivElement = ({
  className = '',
  url,
  ref,
  onClick,
}: {
  className?: string;
  url: string;
  ref?: Ref<HTMLDivElement>;
  onClick?: MouseEventHandler<HTMLDivElement>;
}) => (
  <div
    ref={ref}
    className={cn('w-[24px] h-[24px] bg-no-repeat bg-center bg-contain', className)}
    style={{ backgroundImage: `url(${url})` }}
    onClick={onClick}
  />
);

/**
 * theme, active 별 icon 경로 구분 함수
 * @param lightUrl 라이트모드 icon
 * @param darkUrl 다크모드 icon
 * @param activeUrl active icon
 * @returns HTMLDivElement
 * @author Lian
 */
const themeIcon =
  (lightUrl: string, darkUrl: string, activeUrl?: string) =>
  ({
    className = '',
    onClick,
  }: {
    className?: string;
    onClick?: MouseEventHandler<HTMLDivElement>;
  }) => {
    const { theme } = useTheme();
    const ref = useRef<HTMLDivElement>(null);
    const [isActive, setIsActive] = useState(false);

    useEffect(() => {
      const parent = ref.current?.parentElement;
      if (!parent) {
        return;
      }

      const updateState = () => {
        const { state } = parent.dataset;
        setIsActive(state === 'active' || state === 'on' || state === 'open');
      };

      const observer = new MutationObserver(updateState);
      observer.observe(parent, {
        attributes: true,
        attributeFilter: ['data-state'],
      });

      updateState();

      return () => observer.disconnect();
    }, []);

    let url: string;

    if (isActive && activeUrl) {
      url = activeUrl;
    } else {
      url = theme === 'dark' ? darkUrl : lightUrl;
    }

    return iconDivElement({ className, url, ref, onClick });
  };

const Logo = themeIcon(iconLogo, iconLogoDark);
const Download = themeIcon(iconDown, iconDownDark, iconDownDark);
const MainPageLogo = themeIcon(iconMainPageLogo, iconMainPageLogoDark);
const Shortcut = themeIcon(iconShortcut, iconShortcut);
const Logout = themeIcon(iconLogout, iconLogout);
const MenuLog = themeIcon(iconMenuLog, iconMenuLogDark);
const MenuUserInfo = themeIcon(iconMenuUserInfo, iconMenuUserInfoDark);
const Bin = themeIcon(iconBin, iconBin);
const ModalInfo = themeIcon(iconModalInfo, iconModalInfoDark);
const ModalConfirm = themeIcon(iconModalConfirm, iconModalConfirmDark);
const ModalError = themeIcon(iconModalError, iconModalErrorDark);
const Lock = themeIcon(iconLock, iconLock);
const Pin = themeIcon(iconPin, iconPin);
const Board = themeIcon(iconBoard, iconBoardDark);
const DownloadWithText = themeIcon(iconDownloadWithText, iconDownloadWithText);
const Hamburger = themeIcon(iconHamburger, iconHamburgerDark);
const Search = themeIcon(iconSearch, iconSearch);
const Write = themeIcon(iconWrite, iconWrite);
const Attachment = themeIcon(iconAttachment, iconAttachment);
const Login = themeIcon(iconLogin, iconLogin);
const Manage = themeIcon(iconManage, iconManageDark);
const Profile = themeIcon(iconProfile, iconProfileDark);
const MenuDashboard = themeIcon(iconMenuDashboard, iconMenuDashboardDark);
const MenuData = themeIcon(iconMenuData, iconMenuDataDark);
const MenuDataGroup = themeIcon(iconMenuDataGroup, iconMenuDataGroupDark);
const MenuRole = themeIcon(iconMenuRole, iconMenuRoleDark);
const MenuLink = themeIcon(iconMenuLink, iconMenuLinkDark);
const MenuTool = themeIcon(iconMenuTool, iconMenuToolDark);
const MenuCode = themeIcon(iconMenuCode, iconMenuCodeDark);
const MenuStatistic = themeIcon(iconMenuStatistic, iconMenuStatisticDark);
const MenuUpdate = themeIcon(iconMenuUpdate, iconMenuUpdateDark);
const Excel = themeIcon(iconExcel, iconExcel);
const Box = themeIcon(iconBox, iconBox);
const DashboardBarChart = themeIcon(iconDashboardBarChart, iconDashboardBarChart);
const DashboardLink = themeIcon(iconDashboardLink, iconDashboardLink);

export const Icons = {
  Logo,
  Location,
  Download,
  Animation,
  MainPageLogo,
  Shortcut,
  Logout,
  MenuLog,
  MenuUserInfo,
  Bin,
  ModalInfo,
  ModalConfirm,
  ModalError,
  Lock,
  Pin,
  Board,
  DownloadWithText,
  Hamburger,
  Search,
  Write,
  Attachment,
  Manage,
  Profile,
  Login,
  MenuDashboard,
  MenuData,
  MenuDataGroup,
  MenuRole,
  MenuLink,
  MenuTool,
  MenuCode,
  MenuStatistic,
  MenuUpdate,
  Excel,
  Box,
  DashboardBarChart,
  DashboardLink,
};

export type IconKeys = keyof typeof Icons;
